import { api } from '../../lib/api';

/**
 * Standard Incoterms 2020 rules. Fixed international standard, so this is a
 * built-in list rather than a configurable one.
 */
export const INCOTERMS = [
  { value: 'EXW', label: 'EXW — Ex Works' },
  { value: 'FCA', label: 'FCA — Free Carrier' },
  { value: 'FAS', label: 'FAS — Free Alongside Ship' },
  { value: 'FOB', label: 'FOB — Free on Board' },
  { value: 'CFR', label: 'CFR — Cost and Freight' },
  { value: 'CIF', label: 'CIF — Cost, Insurance and Freight' },
  { value: 'CPT', label: 'CPT — Carriage Paid To' },
  { value: 'CIP', label: 'CIP — Carriage and Insurance Paid To' },
  { value: 'DAP', label: 'DAP — Delivered at Place' },
  { value: 'DPU', label: 'DPU — Delivered at Place Unloaded' },
  { value: 'DDP', label: 'DDP — Delivered Duty Paid' },
];

export const incotermLabel = (value) =>
  INCOTERMS.find((i) => i.value === value)?.label || value;

/**
 * A broad ISO 4217 short-list used when picking which currencies an organization
 * transacts in. Not exhaustive, but covers the common hospitality markets.
 */
export const CURRENCY_OPTIONS = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'CHF', 'JPY', 'CNY', 'HKD', 'SGD',
  'AED', 'SAR', 'QAR', 'INR', 'ZAR', 'MXN', 'BRL', 'SEK', 'NOK', 'DKK', 'PLN',
  'CZK', 'HUF', 'TRY', 'THB', 'IDR', 'MYR', 'PHP', 'KRW', 'RON', 'BGN',
].map((c) => ({ value: c, label: c }));

export const settingsApi = {
  // Organization-level commercial defaults
  getOrg: () => api.get('/settings/commercial'),
  updateOrg: (payload) => api.put('/settings/commercial', payload),

  // Per-property commercial override
  getPropertyCommercial: (propertyId) => api.get(`/settings/commercial/properties/${propertyId}`),
  updatePropertyCommercial: (propertyId, payload) => api.put(`/settings/commercial/properties/${propertyId}`, payload),

  // Resolved (effective) standards for filling supplier dropdowns
  getEffective: (propertyId) =>
    api.get(`/settings/commercial/effective${propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''}`),

  // Organization-level finance coding (cost centres + GL accounts)
  getCoding: () => api.get('/settings/coding'),
  updateCoding: (payload) => api.put('/settings/coding', payload),

  // Organization-level approval workflows
  getOrgApproval: () => api.get('/settings/approval'),
  updateOrgApproval: (payload) => api.put('/settings/approval', payload),

  // Per-property approval override
  getPropertyApproval: (propertyId) => api.get(`/settings/approval/properties/${propertyId}`),
  updatePropertyApproval: (propertyId, payload) => api.put(`/settings/approval/properties/${propertyId}`, payload),

  // Resolved (effective) approval workflows
  getEffectiveApproval: (propertyId) =>
    api.get(`/settings/approval/effective${propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''}`),

  // Requisition rules + the org-wide upload ceiling (org default + per-property override)
  updateOrgRequisitions: (payload) => api.put('/settings/requisitions', payload),
  updatePropertyRequisitions: (propertyId, payload) =>
    api.put(`/settings/requisitions/properties/${propertyId}`, payload),
  getEffectiveRequisitions: (propertyId) =>
    api.get(`/settings/requisitions/effective${propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''}`),

  // Receiving rules (org default + per-property override)
  updateOrgReceiving: (payload) => api.put('/settings/receiving', payload),
  updatePropertyReceiving: (propertyId, payload) => api.put(`/settings/receiving/properties/${propertyId}`, payload),
  getEffectiveReceiving: (propertyId) =>
    api.get(`/settings/receiving/effective${propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''}`),

  // Organization-wide budget enforcement (Warn or Block)
  updateOrgBudgetEnforcement: (payload) => api.put('/settings/commercial/budget-enforcement', payload),

  // Invoice three-way-match tolerances (org default + per-property override)
  updateOrgInvoiceMatching: (payload) => api.put('/settings/invoice-matching', payload),
  updatePropertyInvoiceMatching: (propertyId, payload) => api.put(`/settings/invoice-matching/properties/${propertyId}`, payload),
  getEffectiveInvoiceMatching: (propertyId) =>
    api.get(`/settings/invoice-matching/effective${propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''}`),

  // Property entities
  listProperties: () => api.get('/properties'),
  createProperty: (payload) => api.post('/properties', payload),
  updateProperty: (id, payload) => api.putWithId(`/properties/${id}`, id, payload),
};

/** Common IANA timezones for the property form. */
export const TIMEZONE_OPTIONS = [
  'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Bucharest',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Makassar', 'Asia/Singapore',
  'Asia/Hong_Kong', 'Asia/Tokyo', 'Australia/Sydney', 'America/New_York', 'America/Chicago',
  'America/Denver', 'America/Los_Angeles', 'America/Sao_Paulo',
].map((t) => ({ value: t, label: t }));
